import React, { FC, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useRecoilState } from 'recoil';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './SettingsPageContent.scss';
import { settingsSidebarStateAtom } from '../../data-access';
import {
  CalendarSection,
  EmailSection,
  EnergyPreferencesSection,
  FriendsSection,
  ProfileSection,
  SubscriptionSection,
  TodosSection,
  WearablesSection,
} from './ParentSections/AccountSection';
import { HelpFaqSection } from './ParentSections/AboutSection';
import { AIAssistantSection, CalendlySection } from './ParentSections/FeatureSection';
import {
  GeneralSection,
  NotificationsSection,
  TimeFormatSection,
} from './ParentSections/GeneralSection';
import {
  ChromeExtensionSection,
  MobileAppSection,
  ProductHubSection,
} from './ParentSections/ProductHubSection';
import { SETTINGS_SECTION_PARENT_MAP } from './constants';

interface SettingsPageContentProps {
  className?: string;
}

const SettingsPageContent: FC<SettingsPageContentProps> = ({ className }) => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const [settingsSidebarState, setSettingsSidebarState] = useRecoilState(settingsSidebarStateAtom);
  const { selectedParentKey } = settingsSidebarState;
  const selectedHashSectionKey = useMemo(
    () => decodeURIComponent(hash.replace('#', '').trim()),
    [hash]
  );

  const selectedSectionContent = useMemo(() => {
    const sectionByParentKey: Record<string, React.ReactNode> = {
      profile: <ProfileSection />,
      calendars: <CalendarSection />,
      general: <GeneralSection />,
      wearables: <WearablesSection />,
      todos: <TodosSection />,
      energyPreferences: <EnergyPreferencesSection />,
      email: <EmailSection />,
      manageFriends: <FriendsSection />,
      subscription: <SubscriptionSection />,
      timeFormat: <TimeFormatSection />,
      notifications: <NotificationsSection />,
      aiAssistant: <AIAssistantSection />,
      calendly: <CalendlySection />,
      productHub: <ProductHubSection />,
      mobileApp: <MobileAppSection />,
      chromeExtension: <ChromeExtensionSection />,
      helpFaq: <HelpFaqSection />,
    };

    return sectionByParentKey[selectedParentKey] || sectionByParentKey.profile;
  }, [selectedParentKey]);

  useEffect(() => {
    if (!selectedHashSectionKey) {
      return;
    }

    const mappedParentKey = SETTINGS_SECTION_PARENT_MAP[selectedHashSectionKey];
    if (!mappedParentKey || mappedParentKey === selectedParentKey) {
      return;
    }

    setSettingsSidebarState((prev) => ({
      ...prev,
      selectedParentKey: mappedParentKey,
      expandedParentKeys: [mappedParentKey],
    }));
  }, [selectedHashSectionKey, selectedParentKey, setSettingsSidebarState]);

  useEffect(() => {
    if (!selectedHashSectionKey) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(selectedHashSectionKey);
      if (!target) {
        return;
      }

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [selectedHashSectionKey, selectedParentKey]);

  return (
    <div className={clsx('settings-page-content', className)}>
      <section className="settings-page-content__content" aria-label={t('settings.aria.content')}>
        <div className="settings-page-content__content-scroll">{selectedSectionContent}</div>
      </section>
    </div>
  );
};

export default SettingsPageContent;
